import GroupIcon from './GroupIcon';
import GlobeIcon from './GlobeIcon';
import ChevronDownIcon from './ChevronDownIcon';
import SnailIcon from './SnailIcon';
import SnailStrikeThroughIcon from './SnailStrikeThroughIcon';
import SettingsIcon from './SettingsIcon';
import CloseIcon from './CloseIcon';
import useCopy from '../util/useCopy';
import { OneLeagueResourceType } from '../resources/League/One';
import { uniq } from 'lodash';

type DesktopLeagueControlsProps = {
  leagues: NonNullable<OneLeagueResourceType>[];
  completedBallots: Record<string, boolean>;
  focusedLeagueId: string | undefined;
  onSelectLeague: (leagueId: string | undefined) => void;
  onOpenDelayModal?: () => void;
  onDismissDelay?: () => void;
  onOpenSettingsModal: (leagueId: string) => void;
  delayMs?: number;
};

const DesktopLeagueControls = ({
  leagues,
  completedBallots,
  focusedLeagueId,
  onSelectLeague,
  onOpenDelayModal,
  onDismissDelay,
  onOpenSettingsModal,
  delayMs,
}: DesktopLeagueControlsProps) => {
  const copy = useCopy();
  const completedCount = Object.keys(completedBallots).length;

  const focusedLeague = leagues.find((league) => league.id === focusedLeagueId);

  const getLeagueCount = (league: NonNullable<OneLeagueResourceType>) =>
    uniq([league.ownerUserId, ...league.memberUserIds]).filter(
      (userId) => completedBallots[userId],
    ).length;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onSelectLeague(value === '' ? undefined : value);
  };

  const isLive = !delayMs;
  const showDiscovery =
    (delayMs === undefined || delayMs === null) && onOpenDelayModal;

  return (
    <div className="flex items-center gap-2 p-4">
      <div className="min-w-0">
        <div className="bg-neutral-0 relative inline-flex max-w-full cursor-pointer items-center rounded-2xl border-4 border-neutral-200 font-medium shadow-md">
          <div className="relative flex items-center px-4 py-1.5">
            {focusedLeague ? (
              <>
                <GroupIcon className="mr-1 h-4 w-4 flex-none text-rose-500" />
                <div className="mr-3 flex-none font-normal text-rose-500">
                  {getLeagueCount(focusedLeague)}
                </div>
                <div className="text-rose-dark truncate">
                  {focusedLeague.name}
                </div>
              </>
            ) : (
              <>
                <GlobeIcon className="text-rose-default mr-1 h-4 w-4 flex-none" />
                <div className="text-rose-default mr-3 flex-none font-normal">
                  {completedCount}
                </div>
                <div className="text-rose-dark">{copy.everyone}</div>
              </>
            )}
            <ChevronDownIcon className="ml-2 h-4 w-4 flex-none text-rose-dark" />
            <select
              value={focusedLeagueId ?? ''}
              onChange={handleChange}
              className="absolute inset-0 z-10 cursor-pointer opacity-0"
            >
              <option value="">
                ({completedCount}) {copy.everyone}
              </option>
              {leagues.map((league) => (
                <option key={league.id} value={league.id}>
                  ({getLeagueCount(league)}) {league.name}
                </option>
              ))}
            </select>
          </div>
          {focusedLeague && (
            <button
              className="flex cursor-pointer items-center border-l-2 border-neutral-100 px-2.5 py-1.5 text-rose-dark"
              onClick={() => onOpenSettingsModal(focusedLeague.id)}
            >
              <SettingsIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {onOpenDelayModal &&
        (showDiscovery ? (
          <div className="bg-neutral-0 flex h-11 flex-none items-center rounded-2xl border-4 border-neutral-200 shadow-md">
            <button
              onClick={onOpenDelayModal}
              className="flex h-full cursor-pointer items-center gap-1.5 px-4 py-1.5 font-medium text-rose-dark"
            >
              <SnailIcon className="h-4 w-4 flex-none text-rose-default" />
              <span className="text-sm">{copy.setADelay}</span>
            </button>
            {onDismissDelay && (
              <button
                onClick={onDismissDelay}
                className="flex h-full cursor-pointer items-center border-l-2 border-neutral-100 px-2.5 py-1.5 text-neutral-400"
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        ) : (
          <button
            onClick={onOpenDelayModal}
            className="bg-neutral-0 flex h-11 flex-none cursor-pointer items-center justify-center gap-1.5 rounded-2xl border-4 border-neutral-200 px-3 shadow-md"
          >
            {isLive ? (
              <SnailStrikeThroughIcon className="h-4 w-4 text-neutral-400" />
            ) : (
              <>
                <SnailIcon className="h-4 w-4 text-rose-dark" />
                <span className="text-rose-dark text-sm font-medium">
                  {Math.round((delayMs || 0) / 1000)}s
                </span>
              </>
            )}
          </button>
        ))}
    </div>
  );
};

export default DesktopLeagueControls;
